import React, { useMemo } from "react";
import { Badge, cn } from "@lemonadetraining/web-ui";
import {
  personalizeWorkout,
  type Movement,
  type OneRepMax,
  type Personalized,
  type WorkoutData,
} from "@lemonadetraining/contracts";

import { useEffectiveRegistry } from "../../../hooks/useEffectiveRegistry";
import { useUserAccess } from "../../../hooks/useUserAccess";
import { kindLabel } from "../../../lib/workoutKinds";
import VideoCarousel from "./VideoCarousel";

export interface WorkoutLike {
  workoutData: WorkoutData;
}

interface WorkoutPartViewProps {
  workout: WorkoutLike;
  actions?: React.ReactNode;
  compact?: boolean;
  /** The athlete's current records: % loads then show their own weight. */
  oneRepMaxes?: OneRepMax[];
  /** Offered where a % load has no 1RM to work from. */
  onLogMovement?: (movementID: string) => void;
  /** 4 when the workout sits under a program heading. */
  headingLevel?: 3 | 4;
}

const hasValue = (v: unknown) => v !== undefined && v !== null && v !== "";

/** "5 × 5 @ 75%", "5 sets", "10 reps", "@ 60%"; "" when nothing is prescribed. */
export const prescription = (m: Movement): string => {
  const sets = hasValue(m.sets) && m.sets !== 0 ? m.sets : null;
  const reps = hasValue(m.reps) ? m.reps : null;
  let base = "";
  if (sets !== null && reps !== null) base = `${sets} × ${reps}`;
  else if (sets !== null) base = `${sets} ${sets === 1 ? "set" : "sets"}`;
  else if (reps !== null) base = typeof reps === "number" ? `${reps} reps` : String(reps);
  const load = typeof m.percent === "number" && m.percent > 0 ? `@ ${m.percent}%` : "";
  return [base, load].filter(Boolean).join(" ");
};

export const isBlankMovement = (m: Movement) => !m.movementRegistryID && prescription(m) === "";

const YourWeight: React.FC<{
  item: Personalized;
  name: string;
  onLog?: () => void;
}> = ({ item, name, onLog }) => {
  const { weight, basedOn1RM } = item.personalized;
  if (weight != null) {
    return (
      <span className="block text-sm" data-testid="personal-weight">
        <strong className="tabular-nums">{weight} kg</strong>
        <span className="text-muted-foreground">
          {" "}
          for you{basedOn1RM != null ? `, based on your ${basedOn1RM} kg 1RM` : ""}
        </span>
        {item.warnings.includes("EXCEEDS_1RM") && (
          <span role="alert" className="block text-xs text-destructive">
            This is above your 1RM. Check it with your coach.
          </span>
        )}
        {item.warnings.includes("STALE_1RM") && (
          <span className="block text-xs text-muted-foreground">
            Your 1RM is over six months old.
          </span>
        )}
      </span>
    );
  }
  if (item.warnings.includes("NO_1RM") && onLog) {
    return (
      <button
        type="button"
        onClick={onLog}
        className="block min-h-11 text-left text-sm font-medium underline underline-offset-4"
      >
        Log your {name} 1RM to see your weight
      </button>
    );
  }
  return null;
};

const WorkoutPartView: React.FC<WorkoutPartViewProps> = ({
  workout,
  actions,
  compact = false,
  oneRepMaxes,
  onLogMovement,
  headingLevel = 3,
}) => {
  const { boxID } = useUserAccess();
  const { movements: registry } = useEffectiveRegistry(boxID);
  const data = workout.workoutData;
  const Heading = headingLevel === 4 ? "h4" : "h3";

  const tags = (data.tags ?? []).filter((t) => typeof t === "string" && t.trim() !== "");
  const title = data.title?.trim() || "Untitled workout";
  const description = data.description?.trim() ?? "";
  const movements = useMemo(
    () => (data.movements ?? []).filter((m) => m && !isBlankMovement(m)),
    [data.movements]
  );
  const videos = (data.youTubeIDs ?? []).filter((id) => typeof id === "string" && id !== "");

  const nameOf = (m: Movement) =>
    registry.find((entry) => entry.id === m.movementRegistryID)?.name ?? "Movement";

  const personal = useMemo(
    () =>
      oneRepMaxes
        ? personalizeWorkout(
            movements.map((m) => ({
              movementID: m.movementRegistryID ?? "",
              movementName: "",
              sets: typeof m.sets === "number" ? m.sets : 0,
              reps: m.reps ?? 0,
              percentage: typeof m.percent === "number" ? m.percent : 0,
            })),
            oneRepMaxes
          )
        : null,
    [oneRepMaxes, movements]
  );

  return (
    <article className={cn("flex flex-col", compact ? "gap-2" : "gap-3")}>
      <div className={cn("flex items-center gap-2", actions && "-mr-2 -mt-2")}>
        <Heading
          className={cn(
            "min-w-0 flex-1 font-semibold break-words",
            compact ? "text-sm" : "text-base"
          )}
        >
          {title}
        </Heading>
        {tags.length > 0 && (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
            {tags.map((tag, i) => (
              <Badge key={`${tag}-${i}`} variant="outline">
                {kindLabel(tag)}
              </Badge>
            ))}
          </div>
        )}
        {actions && <div className="flex shrink-0 items-center">{actions}</div>}
      </div>
      {videos.length > 0 &&
        (compact ? (
          <p className="text-xs text-muted-foreground">
            {videos.length === 1 ? "1 video" : `${videos.length} videos`}
          </p>
        ) : (
          <VideoCarousel youTubeIDs={videos} title={data.title} />
        ))}
      {description && (
        <p
          data-testid="workout-description"
          className={cn(
            "whitespace-pre-line break-words text-sm",
            compact && "line-clamp-4 text-muted-foreground"
          )}
        >
          {description}
        </p>
      )}
      {movements.length > 0 && (
        <ul aria-label="Movements" className="flex flex-col gap-1 text-sm">
          {movements.map((m, i) => {
            const detail = prescription(m);
            const registryID = m.movementRegistryID;
            return (
              <li key={m.id ?? `row-${i}`} className="break-words">
                <span className="font-medium">{nameOf(m)}</span>
                {detail && <span className="text-muted-foreground"> · {detail}</span>}
                {personal?.[i] && (
                  <YourWeight
                    item={personal[i]}
                    name={nameOf(m)}
                    onLog={
                      onLogMovement && registryID ? () => onLogMovement(registryID) : undefined
                    }
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </article>
  );
};

export default WorkoutPartView;
